import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { timeEntriesApi, employeesApi, schedulesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Search,
  Edit2,
  Clock,
  Calendar as CalendarIcon,
  Image,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Plus,
  RefreshCw,
  Users,
  LayoutList,
  Download,
  Trash2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  differenceInDays,
} from 'date-fns';
import { useTimeFormat } from '../utils/timeFormat';

const TimeEntries = () => {
  const { user, selectedLocation } = useAuth();
  const fmtTime = useTimeFormat();
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('week'); // 'week', 'day', 'range'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [editingEntry, setEditingEntry] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [groupMode, setGroupMode] = useState('date'); // 'date' or 'employee'

  // Manual entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    employee_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    clock_in_time: '09:00',
    clock_out_date: '',
    clock_out_time: '17:00',
    break_minutes: 0,
  });

  const canCreateManual = user?.role === 'admin' || user?.role === 'accounting_admin' || user?.role === 'manager';

  // Calculate week boundaries (Monday to Sunday)
  const getWeekBoundaries = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday
    const end = endOfWeek(date, { weekStartsOn: 1 }); // Sunday
    return { start, end };
  };

  const { start: weekStart, end: weekEnd } = getWeekBoundaries(selectedDate);

  // Load data
  useEffect(() => {
    loadData();
  }, [selectedLocation, selectedDate, viewMode, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      let startDate, endDate;

      if (viewMode === 'day') {
        startDate = format(selectedDate, 'yyyy-MM-dd');
        endDate = startDate;
      } else if (viewMode === 'range' && dateRange.from && dateRange.to) {
        startDate = format(dateRange.from, 'yyyy-MM-dd');
        endDate = format(dateRange.to, 'yyyy-MM-dd');
      } else {
        // Default: week view
        startDate = format(weekStart, 'yyyy-MM-dd');
        endDate = format(weekEnd, 'yyyy-MM-dd');
      }

      const [entriesData, employeesData, schedulesData] = await Promise.all([
        timeEntriesApi.getAll({
          locationId: selectedLocation,
          startDate,
          endDate,
        }),
        employeesApi.getAll(selectedLocation),
        schedulesApi.getAll({
          locationId: selectedLocation,
          startDate,
          endDate,
        }).catch(() => []),
      ]);

      setTimeEntries(entriesData);
      setEmployees(employeesData);
      setSchedules(schedulesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries
  const filteredEntries = useMemo(() => {
    return timeEntries.filter((entry) => {
      const emp = employees.find((e) => e.id === entry.employee_id);
      const matchesSearch =
        !searchQuery ||
        emp?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp?.employee_number?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [timeEntries, employees, searchQuery]);

  // Group entries by date
  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach((entry) => {
      if (!groups[entry.date]) {
        groups[entry.date] = [];
      }
      groups[entry.date].push(entry);
    });
    return Object.entries(groups).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  }, [filteredEntries]);

  // Group entries by employee
  const groupedByEmployee = useMemo(() => {
    const groups = {};
    filteredEntries.forEach((entry) => {
      const emp = employees.find((e) => e.id === entry.employee_id);
      const name = emp?.name || 'Unknown';
      if (!groups[entry.employee_id]) {
        groups[entry.employee_id] = { name, entries: [] };
      }
      groups[entry.employee_id].entries.push(entry);
    });
    // Sort alphabetically by name
    return Object.entries(groups)
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([empId, data]) => ({
        empId,
        name: data.name,
        entries: data.entries.sort((a, b) => a.date.localeCompare(b.date)),
      }));
  }, [filteredEntries, employees]);

  // Calculate weekly hours per employee (for overtime indicator)
  const employeeWeeklyHours = useMemo(() => {
    const hours = {};
    filteredEntries.forEach((entry) => {
      if (!hours[entry.employee_id]) hours[entry.employee_id] = 0;
      hours[entry.employee_id] += entry.hours_worked || 0;
    });
    return hours;
  }, [filteredEntries]);

  // Calculate Regular/Overtime hours per entry based on Mon-Sun work week
  const entryOvertimeMap = useMemo(() => {
    const map = {};
    // Group ALL time entries (not just filtered) by employee to get full week context
    const byEmp = {};
    timeEntries.forEach((e) => {
      if (!byEmp[e.employee_id]) byEmp[e.employee_id] = [];
      byEmp[e.employee_id].push(e);
    });

    Object.entries(byEmp).forEach(([empId, empEntries]) => {
      // Group entries by Mon-Sun week
      const weekGroups = {};
      empEntries.forEach((entry) => {
        const d = parseISO(entry.date);
        const monday = startOfWeek(d, { weekStartsOn: 1 });
        const weekKey = format(monday, 'yyyy-MM-dd');
        if (!weekGroups[weekKey]) weekGroups[weekKey] = [];
        weekGroups[weekKey].push(entry);
      });

      // For each week, sort by date and allocate regular/OT
      Object.values(weekGroups).forEach((weekEntries) => {
        weekEntries.sort((a, b) => a.date.localeCompare(b.date) || (a.clock_in || '').localeCompare(b.clock_in || ''));
        let cumulative = 0;
        weekEntries.forEach((entry) => {
          const hours = entry.hours_worked || 0;
          const prevCumulative = cumulative;
          cumulative += hours;
          // Regular = hours up to 40 mark
          const regEnd = Math.min(cumulative, 40);
          const regular = Math.max(regEnd - prevCumulative, 0);
          const overtime = Math.max(hours - regular, 0);
          map[entry.id] = { regular: Math.round(regular * 100) / 100, overtime: Math.round(overtime * 100) / 100 };
        });
      });
    });
    return map;
  }, [timeEntries]);

  // Helper: Get lateness info for an entry by comparing to schedule.
  //
  // Rules (per user spec):
  //  - Late is only calculated when the employee has a scheduled start time for that specific date.
  //  - Compare the employee's actual clock-in vs their scheduled start_time.
  //  - Clock in on time or early => 0 min late (shows "On Time").
  //  - No schedule for that date => "No Schedule" (does not appear as late anywhere).
  //
  // We match ONLY by employee_id + date (never by name) to prevent unscheduled employees from
  // being falsely attributed to another employee's shift.
  const getLateInfo = (entry) => {
    if (!entry.clock_in) return { text: '-', minutes: 0, isLate: false, hasSchedule: false };
    const sched = schedules.find(
      (s) => s.employee_id === entry.employee_id && s.date === entry.date && !s.is_open_shift
    );
    if (!sched || !sched.start_time) {
      return { text: 'No Schedule', minutes: 0, isLate: false, hasSchedule: false };
    }

    // Parse schedule start_time (e.g., "10:00", "09:30")
    const parts = sched.start_time.split(':');
    if (parts.length < 2) return { text: '-', minutes: 0, isLate: false, hasSchedule: false };
    const schedH = parseInt(parts[0], 10);
    const schedM = parseInt(parts[1], 10);
    if (Number.isNaN(schedH) || Number.isNaN(schedM)) {
      return { text: '-', minutes: 0, isLate: false, hasSchedule: false };
    }

    // Parse actual clock-in time — MUST use the location-local rendering so we don't mix
    // browser timezone against the location's schedule time.
    let actualH = null;
    let actualM = null;
    if (entry.clock_in_local) {
      const m12 = entry.clock_in_local.match(/(\d+):(\d+)\s*(AM|PM)/i);
      const m24 = entry.clock_in_local.match(/^(\d{1,2}):(\d{2})$/);
      if (m12) {
        actualH = parseInt(m12[1], 10);
        actualM = parseInt(m12[2], 10);
        const ampm = m12[3].toUpperCase();
        if (ampm === 'PM' && actualH !== 12) actualH += 12;
        if (ampm === 'AM' && actualH === 12) actualH = 0;
      } else if (m24) {
        actualH = parseInt(m24[1], 10);
        actualM = parseInt(m24[2], 10);
      }
    }
    if (actualH === null || actualM === null) {
      // Missing location-local rendering — we can't reliably compute against the local
      // scheduled start_time without a timezone, so return blank rather than a false number.
      return { text: '-', minutes: 0, isLate: false, hasSchedule: true };
    }

    const diffMinutes = (actualH * 60 + actualM) - (schedH * 60 + schedM);
    if (diffMinutes <= 0) return { text: 'On Time', minutes: 0, isLate: false, hasSchedule: true };
    return { text: `${diffMinutes} min late`, minutes: diffMinutes, isLate: true, hasSchedule: true };
  };

  // Helper: Compute meaningful status for a time entry
  const getEntryStatus = (entry) => {
    if (entry.is_manual) return { label: 'Manual', color: 'bg-blue-100 text-blue-700' };
    if (entry.clock_in && entry.clock_out) return { label: 'Completed', color: 'bg-green-100 text-green-700' };
    if (entry.clock_in && !entry.clock_out) {
      const today = format(new Date(), 'yyyy-MM-dd');
      if (entry.date < today) return { label: 'Missed Clock-Out', color: 'bg-red-100 text-red-700' };
      return { label: 'Active', color: 'bg-purple-100 text-purple-700' };
    }
    return { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' };
  };

  // Navigation
  const handlePrevWeek = () => setSelectedDate(subWeeks(selectedDate, 1));
  const handleNextWeek = () => setSelectedDate(addWeeks(selectedDate, 1));

  const handleCreateManualEntry = async () => {
    if (!manualEntry.employee_id || !manualEntry.date || !manualEntry.clock_in_time) {
      alert('Please fill in employee, date, and clock-in time');
      return;
    }
    try {
      await timeEntriesApi.createManual(manualEntry);
      setShowManualEntry(false);
      setManualEntry({ employee_id: '', date: format(new Date(), 'yyyy-MM-dd'), clock_in_time: '09:00', clock_out_date: '', clock_out_time: '17:00', break_minutes: 0 });
      await loadData();
    } catch (error) {
      alert('Failed to create time entry: ' + error.message);
    }
  };

  const handlePrevDay = () => setSelectedDate(addDays(selectedDate, -1));
  const handleNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const handleToday = () => setSelectedDate(new Date());

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setShowDatePicker(false);
    }
  };

  const handleEditEntry = (entry) => {
    const emp = employees.find((e) => e.id === entry.employee_id);
    setEditingEntry({
      ...entry,
      employeeName: emp?.name,
      clockInDate: entry.clock_in ? format(parseISO(entry.clock_in), 'yyyy-MM-dd') : entry.date,
      clockInTime: entry.clock_in ? format(parseISO(entry.clock_in), 'HH:mm') : '',
      clockOutDate: entry.clock_out ? format(parseISO(entry.clock_out), 'yyyy-MM-dd') : (entry.clock_in ? format(parseISO(entry.clock_in), 'yyyy-MM-dd') : entry.date),
      clockOutTime: entry.clock_out ? format(parseISO(entry.clock_out), 'HH:mm') : '',
    });
  };

  const handleSaveEntry = async () => {
    if (!editingEntry) return;
    setSaving(true);

    try {
      // Build full ISO timestamps from the local date+time inputs.
      // Backend will recompute hours_worked authoritatively.
      const [inHours, inMins] = editingEntry.clockInTime.split(':').map(Number);
      const clockIn = new Date(`${editingEntry.clockInDate}T00:00:00`);
      clockIn.setHours(inHours, inMins, 0);

      let clockOutIso = null;
      if (editingEntry.clockOutTime) {
        const [outHours, outMins] = editingEntry.clockOutTime.split(':').map(Number);
        const clockOut = new Date(`${editingEntry.clockOutDate || editingEntry.clockInDate}T00:00:00`);
        clockOut.setHours(outHours, outMins, 0);
        clockOutIso = clockOut.toISOString();
      }

      await timeEntriesApi.update(editingEntry.id, {
        clock_in: clockIn.toISOString(),
        clock_out: clockOutIso,
        break_minutes: parseInt(editingEntry.break_minutes) || 0,
      });

      // Reload data so every view picks up the recomputed hours/locals
      await loadData();
      setEditingEntry(null);
    } catch (error) {
      console.error('Failed to save entry:', error);
      alert(error.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!editingEntry) return;
    setDeleting(true);
    try {
      await timeEntriesApi.delete(editingEntry.id);
      await loadData();
      setConfirmDelete(null);
      setEditingEntry(null);
      toast.success('Time entry deleted. Payroll, reports, and totals updated.');
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error(error.message || 'Failed to delete time entry');
    } finally {
      setDeleting(false);
    }
  };

  // Get date display text
  const getDateRangeText = () => {
    if (viewMode === 'day') {
      return format(selectedDate, 'EEEE, MMMM d, yyyy');
    } else if (viewMode === 'range' && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
    }
    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')} (Mon-Sun)`;
  };

  // Shared row renderer for "By Date" view
  const renderEntryRow = (entry) => {
    const emp = employees.find((e) => e.id === entry.employee_id);
    const ot = entryOvertimeMap[entry.id] || { regular: 0, overtime: 0 };
    return (
      <TableRow key={entry.id} className="hover:bg-gray-50" data-testid={`time-entry-${entry.id}`}>
        <TableCell>
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={emp?.avatar} alt={emp?.name} />
              <AvatarFallback className="bg-gray-200 text-xs">
                {emp?.name?.split(' ').map((n) => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-gray-900">{emp?.name || 'Unknown'}</p>
              <p className="text-xs text-gray-500 font-mono">{emp?.employee_number}</p>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-gray-900">
          {fmtTime.local(entry.clock_in_local) || (entry.clock_in ? fmtTime(entry.clock_in) : '-')}
        </TableCell>
        <TableCell className="text-gray-900">
          {entry.clock_out ? (fmtTime.local(entry.clock_out_local) || fmtTime(entry.clock_out)) : <span className="text-purple-600 font-medium">Working</span>}
        </TableCell>
        <TableCell data-testid={`late-clock-in-${entry.id}`}>
          {(() => {
            const late = getLateInfo(entry);
            if (late.text === 'No Schedule') return <span className="text-gray-400 text-xs">No Schedule</span>;
            if (late.text === '-') return <span className="text-gray-400">-</span>;
            return late.isLate ? <span className="text-red-600 font-medium text-sm">{late.text}</span> : <span className="text-green-600 text-sm">{late.text}</span>;
          })()}
        </TableCell>
        <TableCell className="text-gray-600">{entry.break_minutes || 0} min</TableCell>
        <TableCell className="text-right font-medium text-gray-900">{(entry.hours_worked || 0).toFixed(2)}h</TableCell>
        <TableCell className="text-right text-gray-900">{ot.regular.toFixed(2)}h</TableCell>
        <TableCell className="text-right">
          {ot.overtime > 0 ? <span className="text-amber-600 font-medium">{ot.overtime.toFixed(2)}h</span> : <span className="text-gray-400">0.00h</span>}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            {entry.photo_in && <button onClick={() => setShowPhotoModal({ type: 'Clock In', url: entry.photo_in })} className="p-1 bg-purple-100 text-purple-600 rounded text-xs hover:bg-purple-200" title="View Clock In Photo"><Image className="w-4 h-4" /></button>}
            {entry.photo_out && <button onClick={() => setShowPhotoModal({ type: 'Clock Out', url: entry.photo_out })} className="p-1 bg-blue-100 text-blue-600 rounded text-xs hover:bg-blue-200" title="View Clock Out Photo"><Image className="w-4 h-4" /></button>}
            {!entry.photo_in && !entry.photo_out && <span className="text-gray-400 text-xs">None</span>}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            {(() => { const status = getEntryStatus(entry); return <Badge variant="secondary" className={status.color} data-testid={`status-${entry.id}`}>{status.label}</Badge>; })()}
            {entry.notices && entry.notices.length > 0 && <Badge className="bg-amber-100 text-amber-700" title={entry.notices.join(', ')}><AlertTriangle className="w-3 h-3 mr-1" />{entry.notices.length}</Badge>}
            {entry.camera_unavailable && <Badge className="bg-amber-100 text-amber-700" title="No photo - camera unavailable"><AlertTriangle className="w-3 h-3" /></Badge>}
          </div>
        </TableCell>
        <TableCell>
          <Button variant="ghost" size="icon" onClick={() => handleEditEntry(entry)} data-testid={`edit-entry-${entry.id}`}><Edit2 className="w-4 h-4" /></Button>
        </TableCell>
      </TableRow>
    );
  };

  // Row renderer for "By Employee" view (shows date instead of employee name)
  const renderEntryRowForEmployee = (entry) => {
    const ot = entryOvertimeMap[entry.id] || { regular: 0, overtime: 0 };
    return (
      <TableRow key={entry.id} className="hover:bg-gray-50" data-testid={`time-entry-${entry.id}`}>
        <TableCell className="text-gray-900 font-medium">{format(parseISO(entry.date), 'EEE, MMM d')}</TableCell>
        <TableCell className="text-gray-900">
          {fmtTime.local(entry.clock_in_local) || (entry.clock_in ? fmtTime(entry.clock_in) : '-')}
        </TableCell>
        <TableCell className="text-gray-900">
          {entry.clock_out ? (fmtTime.local(entry.clock_out_local) || fmtTime(entry.clock_out)) : <span className="text-purple-600 font-medium">Working</span>}
        </TableCell>
        <TableCell data-testid={`late-clock-in-${entry.id}`}>
          {(() => {
            const late = getLateInfo(entry);
            if (late.text === 'No Schedule') return <span className="text-gray-400 text-xs">No Schedule</span>;
            if (late.text === '-') return <span className="text-gray-400">-</span>;
            return late.isLate ? <span className="text-red-600 font-medium text-sm">{late.text}</span> : <span className="text-green-600 text-sm">{late.text}</span>;
          })()}
        </TableCell>
        <TableCell className="text-gray-600">{entry.break_minutes || 0} min</TableCell>
        <TableCell className="text-right font-medium text-gray-900">{(entry.hours_worked || 0).toFixed(2)}h</TableCell>
        <TableCell className="text-right text-gray-900">{ot.regular.toFixed(2)}h</TableCell>
        <TableCell className="text-right">
          {ot.overtime > 0 ? <span className="text-amber-600 font-medium">{ot.overtime.toFixed(2)}h</span> : <span className="text-gray-400">0.00h</span>}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            {entry.photo_in && <button onClick={() => setShowPhotoModal({ type: 'Clock In', url: entry.photo_in })} className="p-1 bg-purple-100 text-purple-600 rounded text-xs hover:bg-purple-200" title="View Clock In Photo"><Image className="w-4 h-4" /></button>}
            {entry.photo_out && <button onClick={() => setShowPhotoModal({ type: 'Clock Out', url: entry.photo_out })} className="p-1 bg-blue-100 text-blue-600 rounded text-xs hover:bg-blue-200" title="View Clock Out Photo"><Image className="w-4 h-4" /></button>}
            {!entry.photo_in && !entry.photo_out && <span className="text-gray-400 text-xs">None</span>}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            {(() => { const status = getEntryStatus(entry); return <Badge variant="secondary" className={status.color} data-testid={`status-${entry.id}`}>{status.label}</Badge>; })()}
            {entry.notices && entry.notices.length > 0 && <Badge className="bg-amber-100 text-amber-700" title={entry.notices.join(', ')}><AlertTriangle className="w-3 h-3 mr-1" />{entry.notices.length}</Badge>}
          </div>
        </TableCell>
        <TableCell>
          <Button variant="ghost" size="icon" onClick={() => handleEditEntry(entry)} data-testid={`edit-entry-${entry.id}`}><Edit2 className="w-4 h-4" /></Button>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const exportToExcel = async () => {
    if (filteredEntries.length === 0) return;
    const XLSX = await import('xlsx');
    const rows = filteredEntries.map((entry) => {
      const emp = employees.find((e) => e.id === entry.employee_id);
      const ot = entryOvertimeMap[entry.id] || { regular: 0, overtime: 0 };
      const late = getLateInfo(entry);
      const status = getEntryStatus(entry);
      return {
        Date: format(parseISO(entry.date), 'yyyy-MM-dd (EEE)'),
        Employee: emp?.name || 'Unknown',
        'Employee #': emp?.employee_number || '',
        'Clock In': fmtTime.local(entry.clock_in_local) || (entry.clock_in ? fmtTime(entry.clock_in) : '-'),
        'Clock Out': entry.clock_out ? (fmtTime.local(entry.clock_out_local) || fmtTime(entry.clock_out)) : 'Working',
        'Late Clock In': late.text || '-',
        'Break (min)': entry.break_minutes || 0,
        'Hours': Number((entry.hours_worked || 0).toFixed(2)),
        'Regular Hrs': Number(ot.regular.toFixed(2)),
        'Overtime Hrs': Number(ot.overtime.toFixed(2)),
        'Status': status.label,
      };
    });

    // Append grand totals
    rows.push({
      Date: '',
      Employee: 'GRAND TOTAL',
      'Employee #': '',
      'Clock In': '',
      'Clock Out': '',
      'Late Clock In': '',
      'Break (min)': filteredEntries.reduce((s, e) => s + (e.break_minutes || 0), 0),
      'Hours': Number(filteredEntries.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(2)),
      'Regular Hrs': Number(filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.regular || 0), 0).toFixed(2)),
      'Overtime Hrs': Number(filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.overtime || 0), 0).toFixed(2)),
      'Status': '',
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 18 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 18 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Time Entries');
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    XLSX.writeFile(wb, `TimeEntries_${dateStr}.xlsx`);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Time Entries</h1>
            <p className="text-gray-500 mt-1">View and edit employee clock-in/out records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700" title="Refresh" data-testid="refresh-time-entries-btn">
              <RefreshCw className="w-5 h-5" />
            </button>
            <Button
              onClick={() => exportToExcel()}
              variant="outline"
              className="border-green-200 text-green-700 hover:bg-green-50"
              disabled={filteredEntries.length === 0}
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Export Excel
            </Button>
            {canCreateManual && (
              <Button onClick={() => setShowManualEntry(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-manual-entry-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Time Entry
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* View Mode Selector */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'day' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('day')}
                className={viewMode === 'day' ? 'bg-purple-600' : ''}
              >
                Day
              </Button>
              <Button
                variant={viewMode === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('week')}
                className={viewMode === 'week' ? 'bg-purple-600' : ''}
              >
                Week
              </Button>
              <Button
                variant={viewMode === 'range' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('range')}
                className={viewMode === 'range' ? 'bg-purple-600' : ''}
                data-testid="range-view-btn"
              >
                Custom Range
              </Button>
            </div>

            {/* Date Navigation - Different UI based on view mode */}
            {viewMode === 'range' ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <Label className="text-sm text-gray-600">From:</Label>
                  <Input
                    type="date"
                    value={dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value ? parseISO(e.target.value) : null })}
                    className="w-40"
                    data-testid="date-range-from"
                  />
                  <Label className="text-sm text-gray-600">To:</Label>
                  <Input
                    type="date"
                    value={dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value ? parseISO(e.target.value) : null })}
                    className="w-40"
                    data-testid="date-range-to"
                  />
                </div>
                {dateRange.from && dateRange.to && (
                  <Badge variant="secondary">
                    {differenceInDays(dateRange.to, dateRange.from) + 1} days
                  </Badge>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={viewMode === 'day' ? handlePrevDay : handlePrevWeek}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2 min-w-[200px]">
                      <CalendarIcon className="w-4 h-4" />
                      {getDateRangeText()}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={viewMode === 'day' ? handleNextDay : handleNextWeek}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>

                <Button variant="outline" onClick={handleToday}>
                  Today
                </Button>
              </div>
            )}

            {/* Search */}
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search employee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-time-entries"
              />
            </div>

            {/* Group Toggle */}
            <div className="flex items-center gap-1 border rounded-lg p-1" data-testid="group-mode-toggle">
              <Button
                variant={groupMode === 'date' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setGroupMode('date')}
                className={groupMode === 'date' ? 'bg-purple-600 text-white' : 'text-gray-600'}
                data-testid="group-by-date-btn"
              >
                <CalendarIcon className="w-4 h-4 mr-1" /> By Date
              </Button>
              <Button
                variant={groupMode === 'employee' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setGroupMode('employee')}
                className={groupMode === 'employee' ? 'bg-purple-600 text-white' : 'text-gray-600'}
                data-testid="group-by-employee-btn"
              >
                <Users className="w-4 h-4 mr-1" /> By Employee
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Summary - Overtime Indicator */}
      {viewMode === 'week' && Object.keys(employeeWeeklyHours).length > 0 && (
        <Card className="mb-6 border-0 shadow-md bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Weekly Hours Summary (Overtime &gt; 40 hrs)</span>
            </div>
            <div className="flex flex-wrap gap-4">
              {Object.entries(employeeWeeklyHours)
                .filter(([_, hours]) => hours > 40)
                .map(([empId, hours]) => {
                  const emp = employees.find((e) => e.id === empId);
                  const overtimeHours = hours - 40;
                  return (
                    <div key={empId} className="bg-white px-3 py-2 rounded-lg border border-amber-200">
                      <span className="font-medium text-gray-900">{emp?.name || 'Unknown'}</span>
                      <span className="ml-2 text-amber-600 font-semibold">
                        {hours.toFixed(1)}h ({overtimeHours.toFixed(1)}h OT)
                      </span>
                    </div>
                  );
                })}
              {Object.entries(employeeWeeklyHours).filter(([_, hours]) => hours > 40).length === 0 && (
                <span className="text-amber-700">No employees with overtime this week</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table header definition (shared) */}
      {filteredEntries.length > 0 ? (
        <div className="space-y-6">
          {groupMode === 'date' ? (
            <>
              {groupedEntries.map(([date, entries]) => (
                <Card key={date} className="border-0 shadow-md">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                      <Badge variant="secondary" className="ml-2">
                        {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-medium">Employee</TableHead>
                          <TableHead className="font-medium">Clock In</TableHead>
                          <TableHead className="font-medium">Clock Out</TableHead>
                          <TableHead className="font-medium">Late Clock In</TableHead>
                          <TableHead className="font-medium">Break</TableHead>
                          <TableHead className="font-medium text-right">Hours</TableHead>
                          <TableHead className="font-medium text-right">Regular Hrs</TableHead>
                          <TableHead className="font-medium text-right">OT Hrs</TableHead>
                          <TableHead className="font-medium">Photos</TableHead>
                          <TableHead className="font-medium">Status</TableHead>
                          <TableHead className="font-medium w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry) => renderEntryRow(entry))}
                        {/* Date total row */}
                        <TableRow className="bg-gray-100 font-semibold">
                          <TableCell colSpan={4} className="text-gray-700">Total</TableCell>
                          <TableCell className="text-gray-700">{entries.reduce((s, e) => s + (e.break_minutes || 0), 0)} min</TableCell>
                          <TableCell className="text-right text-gray-900">{entries.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(2)}h</TableCell>
                          <TableCell className="text-right text-gray-900">{entries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.regular || 0), 0).toFixed(2)}h</TableCell>
                          <TableCell className="text-right text-amber-700">{entries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.overtime || 0), 0).toFixed(2)}h</TableCell>
                          <TableCell colSpan={3}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
              {/* Grand total */}
              <Card className="border-0 shadow-md bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">Grand Total</span>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gray-600">Break: <strong>{filteredEntries.reduce((s, e) => s + (e.break_minutes || 0), 0)} min</strong></span>
                      <span className="text-gray-900">Hours: <strong>{filteredEntries.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(2)}h</strong></span>
                      <span className="text-green-700">Regular: <strong>{filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.regular || 0), 0).toFixed(2)}h</strong></span>
                      <span className="text-amber-700">Overtime: <strong>{filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.overtime || 0), 0).toFixed(2)}h</strong></span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {groupedByEmployee.map(({ empId, name, entries }) => {
                const emp = employees.find((e) => e.id === empId);
                return (
                  <Card key={empId} className="border-0 shadow-md">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={emp?.avatar} alt={name} />
                            <AvatarFallback className="bg-gray-200 text-xs">
                              {name?.split(' ').map((n) => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="font-semibold">{name}</span>
                            <span className="text-sm text-gray-500 ml-2 font-mono">{emp?.employee_number}</span>
                          </div>
                        </div>
                        <Badge variant="secondary" className="ml-auto">
                          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-medium">Date</TableHead>
                            <TableHead className="font-medium">Clock In</TableHead>
                            <TableHead className="font-medium">Clock Out</TableHead>
                            <TableHead className="font-medium">Late Clock In</TableHead>
                            <TableHead className="font-medium">Break</TableHead>
                            <TableHead className="font-medium text-right">Hours</TableHead>
                            <TableHead className="font-medium text-right">Regular Hrs</TableHead>
                            <TableHead className="font-medium text-right">OT Hrs</TableHead>
                            <TableHead className="font-medium">Photos</TableHead>
                            <TableHead className="font-medium">Status</TableHead>
                            <TableHead className="font-medium w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entries.map((entry) => renderEntryRowForEmployee(entry))}
                          {/* Employee total row */}
                          <TableRow className="bg-gray-100 font-semibold">
                            <TableCell className="text-gray-700">Total</TableCell>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell className="text-gray-700">{entries.reduce((s, e) => s + (e.break_minutes || 0), 0)} min</TableCell>
                            <TableCell className="text-right text-gray-900">{entries.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(2)}h</TableCell>
                            <TableCell className="text-right text-gray-900">{entries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.regular || 0), 0).toFixed(2)}h</TableCell>
                            <TableCell className="text-right text-amber-700">{entries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.overtime || 0), 0).toFixed(2)}h</TableCell>
                            <TableCell colSpan={3}></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
              {/* Grand total */}
              <Card className="border-0 shadow-md bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">Grand Total ({groupedByEmployee.length} employees)</span>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gray-600">Break: <strong>{filteredEntries.reduce((s, e) => s + (e.break_minutes || 0), 0)} min</strong></span>
                      <span className="text-gray-900">Hours: <strong>{filteredEntries.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(2)}h</strong></span>
                      <span className="text-green-700">Regular: <strong>{filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.regular || 0), 0).toFixed(2)}h</strong></span>
                      <span className="text-amber-700">Overtime: <strong>{filteredEntries.reduce((s, e) => s + (entryOvertimeMap[e.id]?.overtime || 0), 0).toFixed(2)}h</strong></span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No time entries found for this period</p>
            <p className="text-sm text-gray-400 mt-1">
              Select a different date or check if employees have clocked in
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Entry Modal */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Edit Time Entry
            </DialogTitle>
          </DialogHeader>

          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{editingEntry.employeeName}</p>
                <p className="text-sm text-gray-500">
                  {format(parseISO(editingEntry.date), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Clock-In Date</Label>
                  <Input
                    type="date"
                    value={editingEntry.clockInDate || ''}
                    onChange={(e) =>
                      setEditingEntry({ ...editingEntry, clockInDate: e.target.value })
                    }
                    data-testid="edit-clock-in-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clock In Time</Label>
                  <Input
                    type="time"
                    value={editingEntry.clockInTime}
                    onChange={(e) =>
                      setEditingEntry({ ...editingEntry, clockInTime: e.target.value })
                    }
                    data-testid="edit-clock-in"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clock-Out Date <span className="text-[10px] text-gray-400">(overnight)</span></Label>
                  <Input
                    type="date"
                    value={editingEntry.clockOutDate || ''}
                    onChange={(e) =>
                      setEditingEntry({ ...editingEntry, clockOutDate: e.target.value })
                    }
                    data-testid="edit-clock-out-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clock Out Time</Label>
                  <Input
                    type="time"
                    value={editingEntry.clockOutTime}
                    onChange={(e) =>
                      setEditingEntry({ ...editingEntry, clockOutTime: e.target.value })
                    }
                    data-testid="edit-clock-out"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Break (minutes) <span className="text-[10px] text-gray-400">— deducted from paid hours</span></Label>
                <Input
                  type="number"
                  min="0"
                  value={editingEntry.break_minutes || 0}
                  onChange={(e) =>
                    setEditingEntry({ ...editingEntry, break_minutes: e.target.value })
                  }
                  data-testid="edit-break"
                />
              </div>

              <div className="bg-amber-50 p-3 rounded-lg text-xs text-amber-700">
                <p>Paid hours auto-recompute on save: <strong>(Clock-Out − Clock-In) − Break</strong>. Edits sync to Dashboard, Payroll, and Reports. Logged in audit trail.</p>
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => setConfirmDelete(editingEntry)}
              disabled={saving || deleting || !canCreateManual}
              data-testid="delete-entry-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Time Entry
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingEntry(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEntry}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={saving || deleting}
                data-testid="save-entry-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Time Entry Modal */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-time-entry-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Time Entry?
            </DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="space-y-3 py-2 text-sm">
              <p className="text-gray-700">
                Are you sure you want to delete this time entry? <strong>This action cannot be undone.</strong>
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs space-y-1">
                <p><strong>Employee:</strong> {confirmDelete.employeeName}</p>
                <p><strong>Date:</strong> {format(parseISO(confirmDelete.date), 'EEEE, MMM d, yyyy')}</p>
                <p><strong>Clock In:</strong> {confirmDelete.clock_in_local || '—'}</p>
                <p><strong>Clock Out:</strong> {confirmDelete.clock_out_local || '—'}</p>
                <p><strong>Hours:</strong> {(confirmDelete.hours_worked || 0).toFixed(2)}h</p>
              </div>
              <p className="text-xs text-gray-500">
                The entry will be removed from payroll, reports, exports, and dashboard totals. The deletion will be recorded in the Audit Log.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteEntry}
              disabled={deleting}
              data-testid="confirm-delete-entry-btn"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Modal */}
      <Dialog open={!!showPhotoModal} onOpenChange={() => setShowPhotoModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{showPhotoModal?.type} Photo</DialogTitle>
          </DialogHeader>
          {showPhotoModal && (
            <div className="rounded-lg overflow-hidden">
              <img
                src={showPhotoModal.url}
                alt={showPhotoModal.type}
                className="w-full h-auto"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Time Entry Modal */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Manual Time Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Employee</Label>
              <Select value={manualEntry.employee_id} onValueChange={(v) => setManualEntry({...manualEntry, employee_id: v})}>
                <SelectTrigger data-testid="manual-entry-employee">
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'active').map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_number})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clock-In Date</Label>
                <Input
                  type="date"
                  value={manualEntry.date}
                  onChange={(e) => setManualEntry({...manualEntry, date: e.target.value})}
                  data-testid="manual-entry-date"
                />
              </div>
              <div>
                <Label>Clock-Out Date (overnight)</Label>
                <Input
                  type="date"
                  value={manualEntry.clock_out_date}
                  onChange={(e) => setManualEntry({...manualEntry, clock_out_date: e.target.value})}
                  placeholder="Leave empty for same-day"
                  data-testid="manual-entry-clock-out-date"
                />
                <p className="text-[10px] text-gray-400 mt-1">Leave empty if shift ends same day</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Clock In</Label>
                <Input
                  type="time"
                  value={manualEntry.clock_in_time}
                  onChange={(e) => setManualEntry({...manualEntry, clock_in_time: e.target.value})}
                  data-testid="manual-entry-clock-in"
                />
              </div>
              <div>
                <Label>Clock Out</Label>
                <Input
                  type="time"
                  value={manualEntry.clock_out_time}
                  onChange={(e) => setManualEntry({...manualEntry, clock_out_time: e.target.value})}
                  data-testid="manual-entry-clock-out"
                />
              </div>
              <div>
                <Label>Break (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualEntry.break_minutes}
                  onChange={(e) => setManualEntry({...manualEntry, break_minutes: parseInt(e.target.value) || 0})}
                  data-testid="manual-entry-break-minutes"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">Paid hours are clock-out minus clock-in, minus the break shown. Times use the location&apos;s timezone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualEntry(false)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleCreateManualEntry}
              disabled={!manualEntry.employee_id || !manualEntry.date || !manualEntry.clock_in_time}
              data-testid="manual-entry-submit"
            >
              Create Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeEntries;
